import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Avatar,
} from '@mui/material';
import {
  Business,
  Visibility,
  Flag,
  CheckCircle,
  TrendingUp,
  People,
  Speed,
  Star,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import missionVisionData from '../data/about/missionVisionData.json';
import coreStrengthsData from '../data/about/coreStrengthsData.json';
import milestonesData from '../data/about/milestonesData.json';


const About = () => {

  const missinVisionIconMap = {
    Flag: <Flag sx={{ fontSize: 40 }} />,
    Visibility: <Visibility sx={{ fontSize: 40 }} />,
  };

  const coreStrengthsDataIconMap = {
    Star: <Star sx={{ fontSize: 40 }} />,
    Business: <Business sx={{ fontSize: 40 }} />,
    Speed: <Speed sx={{ fontSize: 40 }} />,
    CheckCircle: <CheckCircle sx={{ fontSize: 40 }} />,
    People: <People sx={{ fontSize: 40 }} />,
    TrendingUp: <TrendingUp sx={{ fontSize: 40 }} />
  };


  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '2.5rem', md: '3rem' },
              }}
            >
              About VinPriv
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 300,
                fontSize: { xs: '1.2rem', md: '1.5rem' },
              }}
            >
              Your Trusted Partner in IT Talent Solutions
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Company Background */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
                Our Story
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                Founded in February 2024, VinPriv Talent Solutions Private Limited emerged with a clear
                vision to bridge the gap between exceptional IT talent and organizations seeking
                specialized expertise in SAP, Power BI, and Microsoft Azure technologies.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                In just over two years, we have established ourselves as a trusted partner for
                businesses across various industries, delivering high-quality consultants who drive
                digital transformation and business growth.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                Our commitment to excellence, combined with deep domain expertise and a client-first
                approach, has enabled us to build lasting partnerships and achieve remarkable success
                in the competitive IT staffing landscape.
              </Typography>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  bgcolor: 'primary.main',
                  color: 'white',
                  textAlign: 'center',
                }}
              >
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  Company Facts
                </Typography>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="h3" fontWeight="bold">
                      2024
                    </Typography>
                    <Typography variant="body1">Founded</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h3" fontWeight="bold">
                      25+
                    </Typography>
                    <Typography variant="body1">Consultants</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h3" fontWeight="bold">
                      2
                    </Typography>
                    <Typography variant="body1">Projects</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h3" fontWeight="bold">
                      3
                    </Typography>
                    <Typography variant="body1">Clients</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Mission & Vision */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {missionVisionData.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                >
                  <Card sx={{ height: '100%', p: 2 }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          bgcolor: item.bgcolor,
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        {missinVisionIconMap[item.icon]}
                      </Avatar>
                      <Typography variant="h4" component="h3" gutterBottom fontWeight="bold">
                        {item.title}
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                        {item.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Core Strengths */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            color: 'text.primary',
            fontWeight: 'bold',
            mb: 6,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '4px',
              backgroundColor: 'primary.main',
              borderRadius: '2px',
            },
          }}
        >
          Our Core Strengths
        </Typography>
        <Grid container spacing={4}>
          {coreStrengthsData.map((strength, index) => (
            <Grid item xs={12} sm={6} md={4} display="flex">
              <Card
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  p: 2,
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 70,
                      height: 70,
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    {/* {strength.icon} */}
                    {coreStrengthsDataIconMap[strength.icon]}
                  </Avatar>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                    {strength.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {strength.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

          ))}
        </Grid>
      </Container>

      {/* Company Timeline */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{
              color: 'text.primary',
              fontWeight: 'bold',
              mb: 6,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100px',
                height: '4px',
                backgroundColor: 'primary.main',
                borderRadius: '2px',
              },
            }}
          >
            Our Journey
          </Typography>
          <Box sx={{ position: 'relative' }}>
            {milestonesData.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Grid container spacing={4} sx={{ mb: 4 }} alignItems="center">
                  <Grid item xs={12} md={5} sx={{ textAlign: index % 2 === 0 ? 'right' : 'left' }}>
                    {index % 2 === 0 && (
                      <Paper elevation={2} sx={{ p: 3, bgcolor: 'white' }}>
                        <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                          {milestone.title}
                        </Typography>
                        <Typography variant="body1">{milestone.description}</Typography>
                      </Paper>
                    )}
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: 60,
                        height: 60,
                        mx: 'auto',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {milestone.year}
                    </Avatar>
                  </Grid>
                  <Grid item xs={12} md={5} sx={{ textAlign: index % 2 === 1 ? 'left' : 'right' }}>
                    {index % 2 === 1 && (
                      <Paper elevation={2} sx={{ p: 3, bgcolor: 'white' }}>
                        <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                          {milestone.title}
                        </Typography>
                        <Typography variant="body1">{milestone.description}</Typography>
                      </Paper>
                    )}
                  </Grid>
                </Grid>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default About;